import {
  createSlice,
  createAsyncThunk,
  createSelector } from
'@reduxjs/toolkit';
import { ApolloClient, gql, NormalizedCacheObject } from '@apollo/client';
import { RootState } from '@src/redux/store';
import * as TYPES from '@src/redux/types';

const GRAPHQL = gql`
  query getReminderList(
    $filter: reminderFilterInput
    $pagination: paginationInput
    $sort: [sortInput]
  ) {
    getReminderList(filter: $filter, pagination: $pagination, sort: $sort) {
      items {
        id
        type
        recursiveMode
        daysBefore
        organizationId
        emails
        month
        day
        hour
        minute
      }
      pagination {
        pageNumber
        pageLimit
      }
      error {
        message
        statusCode
      }
    }
  }
`;

const initialState: TYPES.ReminderListState = {
  result: null,
  error: null,
  loading: false
};

export const getReminderListRequest = createAsyncThunk<
  TYPES.ReminderList,
  {
    filter?: TYPES.ReminderFilterInput;
    pagination?: TYPES.PaginationInput;
    sort?: TYPES.SortInput[];
  },
  {extra: ApolloClient<NormalizedCacheObject>;}>(

  'reminder/get_reminder_list',
  async (
  params: {
    filter?: TYPES.ReminderFilterInput;
    pagination?: TYPES.PaginationInput;
    sort?: TYPES.SortInput[];
  },
  { extra: client }) =>
  {
    try {
      const response = await client.query({
        query: GRAPHQL,
        variables: params
      });
      return response.data.getReminderList;
    } catch (error) {
      const err = error as {message?: String;statusCode?: String;};
      return Promise.reject({
        message: err.message || 'An error occurred',
        statusCode: err.statusCode || '500'
      });
    }
  }
);

const getReminderListSlice = createSlice({
  name: 'get_reminder_list',
  initialState: initialState,
  reducers: {
    getReminderListClear: (state) => {
      state.result = null;
      state.error = null;
      state.loading = false;
    }
  },
  extraReducers: (builder) => {
    builder.
    addCase(getReminderListRequest.pending, (state) => {
      state.result = null;
      state.error = null;
      state.loading = true;
    }).
    addCase(getReminderListRequest.fulfilled, (state, action) => {
      state.result = action.payload;
      state.loading = false;
    }).
    addCase(getReminderListRequest.rejected, (state, action) => {
      state.error = action.payload as Error || {
        message: 'Failed to fetch',
        statusCode: '500'
      };
      state.loading = false;
    });
  }
});

export const { getReminderListClear } = getReminderListSlice.actions;

export const getReminderList = createSelector(
  (state: RootState) => state.reminder.getReminderList.result,
  (state: RootState) => state.reminder.getReminderList.error,
  (state: RootState) => state.reminder.getReminderList.loading,
  (result, error, loading) => ({
    result,
    error,
    loading
  })
);

export default getReminderListSlice.reducer;